import { ErrorHandler, Injectable, computed, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { LanguageService } from '@core/i18n/language.service';
import { ToastService } from '../components/ui/toast/toast';

export interface ErrorOptions {
  message: string;
  duration?: number;
  action?: string;
}

export interface ErrorModalOptions {
  message: string;
  title?: string;
}

/**
 * Signal-based error service for handling and displaying errors
 * Replaces the RxJS-based ErrorService
 *
 * Usage:
 * ```typescript
 * const errorService = inject(ErrorService);
 *
 * // Show toast error
 * errorService.showSnackbar('Error message', 3000);
 *
 * // Show modal error
 * errorService.showModal('Error message', 'Error Title');
 *
 * // Handle API error
 * errorService.handleApiError(error);
 * ```
 */
@Injectable({ providedIn: 'root' })
export class ApiErrorService {
  private readonly ravenService = inject(ErrorHandler);
  private readonly toastService = inject(ToastService);
  private readonly languageService = inject(LanguageService);

  // State signals for modals
  private readonly _modalOptions = signal<ErrorModalOptions | null>(null);
  private readonly _unauthModal = signal<string | null>(null);

  // Public read-only signals
  readonly modalOptions = this._modalOptions.asReadonly();
  readonly unauthModal = this._unauthModal.asReadonly();

  // Computed signals for checking if modals are active
  readonly hasModal = computed(() => this._modalOptions() !== null);
  readonly hasUnauthModal = computed(() => this._unauthModal() !== null);

  /**
   * Show a toast error notification
   * Uses ToastService to display error messages
   */
  showSnackbar(message: string, duration?: number, action?: string): void {
    this.toastService.show(
      {
        title: message,
        variant: 'error',
        action: action
          ? {
              label: action,
              // eslint-disable-next-line @typescript-eslint/no-empty-function
              onClick: () => {},
            }
          : undefined,
      },
      {
        duration: duration,
      }
    );
  }

  /**
   * Show an error modal
   */
  showModal(message: string, title: string = 'There was an issue.'): void {
    this.ravenService.handleError(message);
    this._modalOptions.set({ message, title });
  }

  /**
   * Clear modal
   */
  clearModal(): void {
    this._modalOptions.set(null);
  }

  /**
   * Show unauthorized modal
   */
  showUnauthModal(): void {
    this._unauthModal.set('');
  }

  /**
   * Clear unauthorized modal
   */
  clearUnauthModal(): void {
    this._unauthModal.set(null);
  }

  /**
   * Handle API error response
   * Automatically shows toast with error message
   * Uses console.error directly to avoid circular dependency with Logger
   */
  handleApiError(err: unknown): void {
    const fallback = this.languageService.translate('messages.errorOccurred');
    try {
      if (err instanceof HttpErrorResponse) {
        const requestId = err.headers?.get('X-Request-ID') ?? null;
        const body: Record<string, unknown> = typeof err.error === 'object' && err.error !== null ? err.error : {};

        if (typeof body['devMessage'] === 'string') {
          // eslint-disable-next-line no-console
          console.error('[API Error]', {
            message: body['devMessage'],
            statusCode: typeof body['statusCode'] === 'number' ? body['statusCode'] : err.status,
            requestId,
            timestamp: new Date().toISOString(),
          });
        }
        const message = typeof body['message'] === 'string' ? body['message'] : null;
        this.showSnackbar(message ?? fallback);
      } else {
        const body: Record<string, unknown> =
          typeof err === 'object' && err !== null ? (err as Record<string, unknown>) : {};
        const message = typeof body['message'] === 'string' ? body['message'] : null;
        this.showSnackbar(message ?? fallback);
      }
    } catch (ex) {
      // eslint-disable-next-line no-console
      console.error('[API Error] Failed to parse error response', ex, String(err));
      this.showSnackbar(fallback);
    }
  }

  /**
   * Handle generic error
   */
  handleError(error: Error | string): void {
    const message = error instanceof Error ? error.message : error;
    this.ravenService.handleError(message);
    this.showSnackbar(message);
  }
}
